import React, { useMemo, useRef, useState } from "react";
import ClassNames from "classnames";
import { useTranslation } from "react-i18next";
import Input from "./Input/Input";
import Suggestions, { SuggestionItem } from "./Suggestions/Suggestions";
import Tag from "./Tag/Tag";
import "./TagsSelect.scss";
import { escapeForRegExp } from "./TagUtils";
import { TagEntities } from "./Tag/TagUtils";
import FormFieldError from "../FormFieldError/FormFieldError";
import { FieldViewBlock, SeemPopover, DotLoader } from "../../components";
import { getDataAnalyticsProps, isUndefinedOrNull } from "../../../utils";
import TextButton from "../Buttons/TextButton/TextButton";
import type { Placement } from "@floating-ui/react";

// Absorbed from the former TagsSelect.d.ts. Kept exported because consumers
// (e.g. AddTagsToResource.tsx) import this type by name.
export interface TagOption {
  id: string | number;
  name: string;
  // Suggestion row text; the selected tag still shows `name`.
  suggestionLabel?: string;
  // Extra text matched by the search, alongside `name`.
  searchText?: string;
  // Hover text for the selected tag; defaults to `name`.
  tooltip?: string;
  [key: string]: any;
}

interface TagsSelectError {
  message?: string;
  type?: string;
}

// Imperative handle exposed by the sibling Input component (still JS while
// converted in parallel). Typed locally at the boundary.
// TODO: replace with the Input component's exported handle type once converted.
interface TagsSelectInputHandle {
  focus: () => void;
}

export interface TagsSelectProps {
  listPlacement?: Placement;
  wrapperClassName?: string;
  "data-testid"?: string;
  size?: "s" | "m" | "l" | "max" | "custom" | "xxs" | "xs" | "xl" | "xxl";
  label?: string;
  noMargin?: boolean;
  labelMini?: boolean;
  labelWidth?: string | number;
  error?: TagsSelectError;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  labelTop?: boolean;
  handleInputChange?: (value: string) => void;
  delimiters?: number[];
  minQueryLength?: number;
  suggestions?: TagOption[];
  allowNew?: boolean;
  allowBackspace?: boolean;
  value?: TagOption[];
  onChange?: (value: TagOption[]) => void;
  maxSuggestionsLength?: number;
  autoFocus?: boolean;
  autoResize?: boolean;
  placeholder?: string;
  // Only used to derive listbox ids; callers that omit it get the same
  // "undefined-list-box" ids the original JS produced.
  name?: string;
  maxHeight?: string;
  isLoading?: boolean;
  newTagTextPrefix?: string;
  newTagTextSuffix?: string;
  onBlur?: () => void;
  required?: boolean;
  hideLabelHelper?: boolean;
  onFocus?: () => void;
  noSuggestionsText?: string;
  inputType?: string;
  viewMode?: boolean;
  description?: React.ReactNode;
  // Match the query anywhere in `name`/`searchText` instead of only at word starts.
  matchAnywhere?: boolean;
  [key: string]: any;
}

const KEYS = {
  ENTER: 13,
  TAB: 9,
  BACKSPACE: 8,
  UP_ARROW: 38,
  DOWN_ARROW: 40,
  COMMA: 188,
};

const TAGS_AMOUNT_TO_SHOW = 2;

const TagsSelect: React.FC<TagsSelectProps> = ({
  listPlacement = "below" as Placement,
  wrapperClassName,
  "data-testid": dataTestId,
  size = "max",
  label,
  noMargin,
  labelMini = false,
  labelWidth,
  error,
  className,
  disabled,
  readOnly = false,
  labelTop,
  handleInputChange,
  delimiters = [KEYS.TAB, KEYS.ENTER, KEYS.COMMA],
  minQueryLength = 2,
  suggestions = [],
  allowNew = false,
  allowBackspace = true,
  value,
  onChange,
  maxSuggestionsLength = 6,
  autoFocus,
  autoResize = true,
  placeholder = "",
  name,
  maxHeight = "92px",
  isLoading = false,
  newTagTextPrefix,
  newTagTextSuffix,
  onBlur = () => {},
  required = false,
  hideLabelHelper = false,
  onFocus = () => {},
  noSuggestionsText,
  inputType = "text",
  viewMode = false,
  description = "",
  matchAnywhere = false,
  ...props
}) => {
  const { t } = useTranslation();

  const [inputFocused, setInputFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [query, setQuery] = useState("");
  const input = useRef<TagsSelectInputHandle>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (handleInputChange) {
      handleInputChange(value);
    }

    setQuery(value);
  };

  const getSingleTag = (value: string): TagOption | undefined => {
    // Check if the user typed in an existing suggestion.
    const match = filteredSuggestions.findIndex((suggestion) => {
      return suggestion.name.search(new RegExp(`^${escapeForRegExp(value)}$`, "i")) === 0;
    });

    const index = selectedIndex === -1 ? match : selectedIndex;

    if (index > -1) {
      return filteredSuggestions[index];
    } else if (allowNew) {
      return { id: value, name: value };
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // when one of the terminating keys is pressed, add current query to the tags.
    if (delimiters.indexOf(e.keyCode) !== -1) {
      (query || selectedIndex > -1) && e.preventDefault();

      if (query.length >= minQueryLength) {
        const enteredValues = query.split(",");

        let tagsToAdd: TagOption[] = [];

        enteredValues.forEach((value) => {
          if (value.trim() === "") {
            return;
          }

          const tag = getSingleTag(value.trim());

          if (!!tag) {
            tagsToAdd.push(tag);
          }
        });

        addTags(tagsToAdd);
      }
    }

    // when backspace key is pressed and query is blank, delete the last tag
    if (e.keyCode === KEYS.BACKSPACE && query.length === 0 && allowBackspace) {
      deleteTag((value?.length ?? 0) - 1);
    }

    if (e.keyCode === KEYS.UP_ARROW) {
      e.preventDefault();

      // if last item, cycle to the bottom
      if (selectedIndex <= 0) {
        setSelectedIndex(filteredSuggestions.length - 1);
      } else {
        setSelectedIndex(selectedIndex - 1);
      }
    }

    if (e.keyCode === KEYS.DOWN_ARROW) {
      e.preventDefault();
      setSelectedIndex((selectedIndex + 1) % filteredSuggestions.length);
    }
  };

  const addNewTags = (tags: SuggestionItem) => {
    const enteredIds = (tags?.id as string)?.split(",");
    const enteredNames = tags?.name?.split(",");

    let formattedTags: TagOption[] = [];

    for (let i = 0; i < enteredIds?.length; i++) {
      const id = enteredIds[i]?.trim();
      const name = enteredNames![i]?.trim();
      const tooltip = enteredIds.length === 1 ? tags?.tooltip : undefined;

      if (id !== "" && !isUndefinedOrNull(name !== "")) {
        formattedTags.push({ id, name, ...(tooltip ? { tooltip } : {}) });
      }
    }

    addTags(formattedTags);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (document.activeElement !== event.target) {
      input.current?.focus();
    }
  };

  const handleBlur = () => {
    setQuery("");
    handleFocus();
    setSelectedIndex(-1);
  };

  const handleFocus = () => {
    if (inputFocused) {
      if (onBlur) {
        onBlur();
      }
    } else {
      if (onFocus) {
        onFocus();
      }
    }
    setInputFocused(!inputFocused);
  };

  const isTagAdded = (
    newTag: TagOption,
    tagsList?: TagOption[]
  ): TagOption | undefined | false => {
    if (!!tagsList) {
      return tagsList?.find((tag) => tag.name === newTag.name);
    } else {
      return false;
    }
  };

  const addTags = (tags: TagOption[]) => {
    if (disabled) {
      return;
    }

    let allTags = [...(value ?? [])];
    tags.forEach((tag) => {
      if (!isTagAdded(tag, allTags)) {
        allTags.push(tag);
      }
    });

    if (allTags.length !== (value?.length ?? 0)) {
      onChange?.(allTags);
    }

    setQuery("");
    setSelectedIndex(-1);
  };

  const deleteTag = (i: number) => {
    if (!readOnly) {
      const tags = [...(value ?? [])].slice(0);
      tags.splice(i, 1);
      onChange?.(tags);
      setQuery("");
    }
  };

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-tags",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "has-label-mini": labelMini,
      "is-disabled": disabled,
      "has-no-margin": noMargin,
      "has-read-only": readOnly,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth,
      "has-size-custom": size === "custom",
      "has-error": !!error,
      "hide-label-helper": hideLabelHelper,
      "has-max-height": !!maxHeight,
    },
    className
  );

  const expandable = useMemo(() => {
    return inputFocused && query.length >= minQueryLength;
  }, [inputFocused, query, minQueryLength]);

  const filteredSuggestions = useMemo<TagOption[]>(() => {
    const escapedQuery = escapeForRegExp(query);
    const regex = new RegExp(
      matchAnywhere ? escapedQuery : `(?:^|\\s)${escapedQuery}`,
      "i"
    );
    const options = suggestions
      .filter(
        (item) =>
          (regex.test(item.name) ||
            (!!item.searchText && regex.test(item.searchText))) &&
          !isTagAdded(item, value)
      )
      .slice(0, maxSuggestionsLength);

    if (options.length === 0 && query) {
      if (allowNew) {
        if (newTagTextPrefix || newTagTextSuffix) {
          return [
            {
              id: query,
              name: query,
              prefix: newTagTextPrefix,
              suffix: newTagTextSuffix,
              disableMarkIt: true,
            },
          ];
        }
      } else {
        if (noSuggestionsText) {
          return [
            {
              id: 0,
              name: noSuggestionsText,
              disabled: true,
              disableMarkIt: true,
            },
          ];
        }
      }
    }

    return options;
  }, [maxSuggestionsLength, query, suggestions, value, matchAnywhere]);

  const firstTags = value?.length ? value?.slice(0, TAGS_AMOUNT_TO_SHOW) : [];
  const additionalTags = value?.length ? value?.slice(TAGS_AMOUNT_TO_SHOW) : [];

  return (
    <div className={classNameWrapper} data-testid={dataTestId}>
      {!viewMode ? (
        <>
          {!!label && (
            <label
              style={{ width: labelWidth ? labelWidth : "" }}
              className={`input-label`}
            >
              {`${t(label)}`}
              {!required && !hideLabelHelper && (
                <span className={"label-helper"}>Optional</span>
              )}
            </label>
          )}

          {!!description && (
            <div className={`input-description`}>{description}</div>
          )}
          <div
            style={maxHeight ? { maxHeight } : undefined}
            className={`input-holder${
              inputFocused ? " focused" : ""
            } input-holder-tags-select`}
          >
            {isLoading ? (
              <div className={`tags-select is-loading`}>
                <div className={`loading-placeholder`}>{placeholder}</div>
                <DotLoader className={"tag-select-loader"} />
              </div>
            ) : (
              <div
                className={`tags-select ${disabled ? "is-disabled" : ""}`}
                onClick={handleClick}
                {...getDataAnalyticsProps(props)}
              >
                <div
                  className="tags-select__selected"
                  aria-live="polite"
                  aria-relevant="additions removals"
                >
                  {!!value &&
                    value?.map((tag, i) => {
                      return (
                        <Tag
                          key={i}
                          tag={{
                            ...tag,
                            disabled: disabled || tag?.disabled,
                            readOnly: readOnly || tag?.readOnly,
                            type: "is-regular",
                          }}
                          chipStyle
                          entity={TagEntities.RESOURCE}
                          onDelete={() => deleteTag(i)}
                        />
                      );
                    })}
                </div>
                <div
                  className="tags-select__search"
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                >
                  <SeemPopover
                    isOpen={expandable}
                    placement={listPlacement}
                    className={"tags-suggestions"}
                    useSameWidth
                    body={
                      <Suggestions
                        key="suggestions"
                        query={query}
                        selectedIndex={selectedIndex}
                        listboxId={`${name}-list-box`}
                        expandable={expandable}
                        suggestions={filteredSuggestions}
                        addTag={(tags) => addNewTags(tags)}
                        data-test={`${dataTestId}-suggestions`}
                      />
                    }
                  >
                    <Input
                      disabled={disabled}
                      data-test={`${dataTestId}-input`}
                      ref={input}
                      query={query}
                      selectedIndex={selectedIndex}
                      listboxId={`${name}-list-box`}
                      autoFocus={autoFocus}
                      autoResize={autoResize}
                      expandable={expandable}
                      placeholder={disabled && value ? "" : placeholder}
                      type={inputType}
                    />
                  </SeemPopover>
                </div>
              </div>
            )}
          </div>
          {error && (
            <FormFieldError
              errorMessage={error.message}
              errorType={error.type}
              isOpen={!inputFocused}
            />
          )}
        </>
      ) : (
        <FieldViewBlock label={label}>
          <div className="tags-select-view-mode">
            <div className="first-tags">
              {firstTags?.map((tag) => tag.name).join(", ")}
            </div>

            {additionalTags?.length > 0 && (
              <SeemPopover
                body={
                  <div className="additional-tags-popover">
                    <ul>
                      {additionalTags?.map((tag) => (
                        <li key={tag.id}>{tag.name}</li>
                      ))}
                    </ul>
                  </div>
                }
              >
                <TextButton
                  label={`+${additionalTags?.length}`}
                  buttonStyle="textLink"
                  type="button"
                />
              </SeemPopover>
            )}
          </div>
        </FieldViewBlock>
      )}
    </div>
  );
};

export default TagsSelect;
